import { PLATFORM_TIME_ZONE } from "@repo/app-types/constants/timeZone";
import {
  AutomationTemplateIds,
  type Automation,
} from "@repo/app-types/entities/automation";
import { formatTimeForDisplay } from "@repo/app-types/entities/schedulingConfiguration";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import {
  allScheduleWeekdays,
  type MonthlySchedule,
  type RecurringSchedule,
  type ScheduleWeekday,
} from "@repo/app-types/scheduling/recurringSchedule";
import { nextScheduledRunAt } from "@repo/ui/scheduling/nextScheduledRun";
import { weekdayLabels } from "@repo/ui/scheduling/recurringScheduleFields";
import { DateTime } from "luxon";

/** Humanizes a monthly schedule, e.g. "The first day", "The last day". */
export function describeMonthlySchedule(schedule: MonthlySchedule): string {
  switch (schedule.kind) {
    case "first_day_of_month":
      return "The first day";
    case "first_weekday_of_month":
      return `The first ${weekdayLabels[schedule.weekday]}`;
    case "last_day_of_month":
      return "The last day";
    default:
      return assertUnreachable(schedule);
  }
}

/** Joins weekday labels in week order, e.g. "Monday, Wednesday & Friday". */
function describeWeekdays(weekdays: readonly ScheduleWeekday[]): string {
  const labels = allScheduleWeekdays
    .filter((weekday) => weekdays.includes(weekday))
    .map((weekday) => weekdayLabels[weekday]);
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

/** Humanizes a trigger, e.g. "Monday & Wednesday at 9:00 AM". */
export function describeTrigger(trigger: RecurringSchedule): string {
  if (trigger.frequency === "hourly") {
    return "Hourly";
  }
  const time = formatTimeForDisplay(trigger.time_of_day);
  switch (trigger.frequency) {
    case "daily":
      return `Daily at ${time}`;
    case "weekly":
      return `${describeWeekdays(trigger.weekdays)} at ${time}`;
    case "monthly":
      return `${describeMonthlySchedule(trigger.schedule)} at ${time}`;
    default:
      return assertUnreachable(trigger);
  }
}

/**
 * Humanizes when an automation runs, for the Trigger column of the automations
 * table, e.g. "Monday & Friday at 8am", "Tuesday at 9am", "Daily".
 */
export function describeAutomationTrigger(
  automation: Pick<Automation, "template_id" | "settings">,
): string {
  const { settings } = automation;
  switch (automation.template_id) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      // Coaching goals go out on a fixed schedule (see the template
      // description); it isn't configurable per automation.
      return "Monday & Friday at 8am";
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT: {
      if (!("trigger" in settings)) {
        return "Weekly";
      }
      return describeTrigger(settings.trigger);
    }
    case AutomationTemplateIds.LEAD_BURNING: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return settings.trigger.frequency === "hourly" ? "Hourly" : "Daily";
    }
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.PERFECT_CALLS:
    case AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT:
    case AutomationTemplateIds.CUSTOM: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return describeTrigger(settings.trigger);
    }
    default:
      assertUnreachable(automation.template_id);
  }
}

/**
 * The automation's next scheduled run for the info popover. We compute a concrete
 * datetime for Playbook score movement only, matching its pre-unification
 * behavior; the other templates keep their humanized cadence, and disabled
 * automations have no next run. The trigger's `time_of_day` is a wall-clock time in
 * PLATFORM_TIME_ZONE — the zone the scheduler builds its crons in —
 * so the shared calculator evaluates in that zone rather than the viewer's
 * local one; a Pacific admin viewing a 09:00 ET automation then sees the real
 * 09:00 ET run, tagged with the zone abbreviation.
 */
export function describeAutomationNextRun(
  automation: Pick<Automation, "template_id" | "settings" | "is_enabled">,
  now: DateTime = DateTime.local(),
): string {
  if (!automation.is_enabled) {
    return "N/A";
  }

  const { settings } = automation;
  if (
    automation.template_id === AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT &&
    "trigger" in settings &&
    // The two frequencies score movement's form offers; the rest of the shared
    // trigger union has no run day to project forward from.
    (settings.trigger.frequency === "weekly" ||
      settings.trigger.frequency === "monthly")
  ) {
    const next = nextScheduledRunAt(settings.trigger, PLATFORM_TIME_ZONE, now);
    if (next) {
      // ZZZZ appends the zone abbreviation ("EST"/"EDT") so the displayed time
      // reads unambiguously in the scheduler's zone.
      return next.toFormat("MMM d, h:mm a ZZZZ");
    }
  }

  return describeAutomationTrigger(automation);
}

/** Formats how long a run took, e.g. "45 sec" or "3 min". */
export function formatRunDuration(
  startedAt: string,
  finishedAt: string,
): string {
  const duration = DateTime.fromISO(finishedAt).diff(
    DateTime.fromISO(startedAt),
  );
  const seconds = Math.max(0, Math.round(duration.as("seconds")));
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  return `${Math.round(seconds / 60)} min`;
}
